// src/AddProperty.js
import "./AddProperty.css";
import { useTranslation } from "react-i18next";
import { useMemo, useRef, useState, useEffect } from "react";
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/teal.css";

import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";

/* ✅ Lucide icons (minimal, Airbnb-ish) */
import {
  X,
  Plus,
  Wifi,
  ParkingSquare,
  Waves,
  Snowflake,
  CookingPot,
  Tv,
  WashingMachine,
  Flame,
  Building2,
  PawPrint,
  CigaretteOff,
  MapPin,
  Tag,
  Users,
  BedDouble,
  Bath,
  SprayCan,
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";

/* ✅ Base API (CRA: env vars MUST start with REACT_APP_) */
const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || "https://api.hellastay.com"
    : "http://localhost:8000";

// Fix Leaflet marker icons (CRA/Vite)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

/**
 * ✅ timezone-safe YYYY-MM-DD
 */
function toISO(value) {
  try {
    if (!value) return null;

    if (typeof value?.format === "function") return value.format("YYYY-MM-DD");

    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, "0");
      const d = String(value.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }

    const dt = new Date(value);
    if (isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

function isoToDate(iso) {
  try {
    const s = String(iso || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const [y, m, d] = s.split("-").map((x) => Number(x));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  } catch {
    return null;
  }
}

function FlyTo({ center, zoom = 12 }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.flyTo(center, zoom, { duration: 0.6 });
  }, [center, zoom, map]);
  return null;
}

function ClickToSetMarker({ onPick }) {
  useMapEvents({
    click(e) {
      onPick?.(e.latlng);
    },
  });
  return null;
}

async function reverseGeocodeGR(lat, lng) {
  const url =
    "https://nominatim.openstreetmap.org/reverse?" +
    new URLSearchParams({
      format: "json",
      lat: String(lat),
      lon: String(lng),
      addressdetails: "1",
    });

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Nominatim reverse failed: ${res.status} ${res.statusText} :: ${text}`);
  }

  const data = await res.json();

  const cc = (data?.address?.country_code || "").toUpperCase();
  if (cc && cc !== "GR") return null;

  return data;
}

async function searchGR(q, signal) {
  const url =
    "https://nominatim.openstreetmap.org/search?" +
    new URLSearchParams({
      q: q.trim(),
      format: "json",
      addressdetails: "1",
      countrycodes: "gr",
      limit: "8",
      "accept-language": "en",
    });

  const res = await fetch(url, {
    signal,
    headers: { Accept: "application/json" },
    referrerPolicy: "strict-origin-when-cross-origin",
  });

  if (!res.ok) return [];
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}

/** ✅ Default prices by room type */
const DEFAULT_PRICE_BY_TYPE = {
  Entire: 80,
  Single: 50,
  Double: 80,
  Triple: 110,
};

const MONTHS = [
  { key: "1", short: "Jan", label: "January" },
  { key: "2", short: "Feb", label: "February" },
  { key: "3", short: "Mar", label: "March" },
  { key: "4", short: "Apr", label: "April" },
  { key: "5", short: "May", label: "May" },
  { key: "6", short: "Jun", label: "June" },
  { key: "7", short: "Jul", label: "July" },
  { key: "8", short: "Aug", label: "August" },
  { key: "9", short: "Sep", label: "September" },
  { key: "10", short: "Oct", label: "October" },
  { key: "11", short: "Nov", label: "November" },
  { key: "12", short: "Dec", label: "December" },
];

function createEmptyMonthlyPrices() {
  return MONTHS.reduce((acc, month) => {
    acc[month.key] = "";
    return acc;
  }, {});
}

function sanitizeMonthlyPrices(monthlyPrices) {
  const src = monthlyPrices && typeof monthlyPrices === "object" ? monthlyPrices : {};
  return MONTHS.reduce((acc, month) => {
    const raw = src[month.key];
    if (raw === "" || raw == null) {
      acc[month.key] = "";
      return acc;
    }
    const n = Number(raw);
    acc[month.key] = Number.isFinite(n) && n > 0 ? n : "";
    return acc;
  }, {});
}

function normalizeProperty(raw) {
  if (!raw || typeof raw !== "object") return raw;

  const id = raw.id ?? raw._id;
  const ownerId = raw.ownerId ?? raw.owner ?? raw.owner?._id;

  const imgsRaw = Array.isArray(raw.images) ? raw.images : [];
  const images = imgsRaw
    .map((img) => {
      if (!img) return null;
      if (typeof img === "string") return img;
      if (typeof img === "object" && typeof img.url === "string") return img.url;
      return null;
    })
    .filter(Boolean);

  return {
    ...raw,
    id: id != null ? String(id) : raw.id,
    ownerId: ownerId != null ? String(ownerId) : raw.ownerId,
    images,
  };
}

/* ===================== small helpers ===================== */
const toInt = (v, fallback = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const normalizeTime = (v, fallback) => {
  const s = String(v || "").trim();
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return fallback;
};

/* ===================== UI helpers (icons) ===================== */
const ChipIcon = ({ Icon, size = 18 }) => <Icon className="ap-ico" size={size} strokeWidth={1.8} />;
const InlineIcon = ({ Icon, size = 16 }) => <Icon className="ap-ico ap-ico--inline" size={size} strokeWidth={1.8} />;

/* ===================== Draft (localStorage) ===================== */
const DRAFT_KEY = "hellastay_addproperty_draft_v1";

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function serializeRoomsForDraft(rooms) {
  const arr = Array.isArray(rooms) ? rooms : [];
  return arr.map((r) => ({
    id: r?.id ?? Date.now(),
    type: String(r?.type || "Single"),
    price: Number(r?.price || 0),
    monthlyPrices: sanitizeMonthlyPrices(r?.monthlyPrices),
    availableDates: (r?.availableDates || []).map(toISO).filter(Boolean),
    reservedDates: Array.isArray(r?.reservedDates) ? r.reservedDates : [],
  }));
}

function hydrateRoomsFromDraft(roomsDraft) {
  const arr = Array.isArray(roomsDraft) ? roomsDraft : [];
  return arr.map((r) => ({
    id: r?.id ?? Date.now(),
    type: String(r?.type || "Single"),
    price: Number(r?.price || 0),
    monthlyPrices: sanitizeMonthlyPrices(r?.monthlyPrices),
    availableDates: (Array.isArray(r?.availableDates) ? r.availableDates : [])
      .map(isoToDate)
      .filter(Boolean),
    reservedDates: Array.isArray(r?.reservedDates) ? r.reservedDates : [],
  }));
}

/* ===================== Wizard steps ===================== */
const STEPS = [
  { key: "basics", label: "Basics" },
  { key: "location", label: "Location" },
  { key: "details", label: "Details" },
  { key: "photos", label: "Photos & Availability" },
];

export default function AddProperty({ addProperty, user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const formRef = useRef(null);

  const [step, setStep] = useState(0);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [wifi, setWifi] = useState(false);
  const [parking, setParking] = useState(false);
  const [pool, setPool] = useState(false);

  const [propertyType, setPropertyType] = useState("apartment");
  const [maxGuests, setMaxGuests] = useState(2);
  const [bedrooms, setBedrooms] = useState(1);
  const [bathrooms, setBathrooms] = useState(1);

  const isRoomsMode = useMemo(() => String(propertyType) === "hotel", [propertyType]);

  const [amenitiesExtra, setAmenitiesExtra] = useState({
    ac: false,
    kitchen: false,
    tv: false,
    washer: false,
    heating: false,
    elevator: false,
    seaView: false,
  });

  const [houseRules, setHouseRules] = useState({
    petsAllowed: false,
    smokingAllowed: false,
    checkInFrom: "15:00",
    checkInTo: "22:00",
    checkOutUntil: "11:00",
  });

  const [fees, setFees] = useState({ cleaningFee: 0 });

  const [discounts, setDiscounts] = useState({
    weeklyPercent: 0,
    monthlyPercent: 0,
  });

  const [monthlyPricesOpen, setMonthlyPricesOpen] = useState({});

  const toggleMonthlyPricesPanel = (roomId) => {
    setMonthlyPricesOpen((prev) => ({
      ...prev,
      [roomId]: !prev[roomId],
    }));
  };

  const toggleAmenity = (key) => {
    setAmenitiesExtra((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [locationObj, setLocationObj] = useState(null);
  const [locQuery, setLocQuery] = useState("");
  const [locResults, setLocResults] = useState([]);
  const [locOpen, setLocOpen] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [locError, setLocError] = useState("");
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const lastQueryRef = useRef("");
  const locCacheRef = useRef(new Map());

  const [rooms, setRooms] = useState([
    {
      id: Date.now(),
      type: "Single",
      price: DEFAULT_PRICE_BY_TYPE.Single,
      monthlyPrices: createEmptyMonthlyPrices(),
      availableDates: [],
      reservedDates: [],
    },
  ]);

  useEffect(() => {
    if (isRoomsMode) return;

    setRooms((prev) => {
      const first = prev?.[0] || null;
      const keepPrice = Number(first?.price || 0) > 0 ? Number(first.price) : DEFAULT_PRICE_BY_TYPE.Entire;

      return [
        {
          id: first?.id ?? Date.now(),
          type: "Entire",
          price: keepPrice,
          monthlyPrices: sanitizeMonthlyPrices(first?.monthlyPrices),
          availableDates: Array.isArray(first?.availableDates) ? first.availableDates : [],
          reservedDates: Array.isArray(first?.reservedDates) ? first.reservedDates : [],
        },
      ];
    });
  }, [isRoomsMode]);

  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [isNarrow, setIsNarrow] = useState(typeof window !== "undefined" ? window.innerWidth < 920 : false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 920);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const basePrice = useMemo(() => Number(rooms?.[0]?.price || 0), [rooms]);

  const canSubmit = useMemo(() => {
    if (!user) return false;
    if (submitting) return false;
    if (!title || !locationObj?.text) return false;
    if (images.length === 0) return false;
    if (rooms.some((r) => !r.availableDates || r.availableDates.length === 0 || Number(r.price || 0) <= 0)) return false;
    return true;
  }, [user, title, locationObj, rooms, images, submitting]);

  const handleAddRoom = () => {
    if (!isRoomsMode) return;
    const id = Date.now();
    setRooms((prev) => [
      ...prev,
      {
        id,
        type: "Single",
        price: DEFAULT_PRICE_BY_TYPE.Single,
        monthlyPrices: createEmptyMonthlyPrices(),
        availableDates: [],
        reservedDates: [],
      },
    ]);
  };

  const handleRemoveRoom = (id) => {
    if (!isRoomsMode) return;
    setRooms((prev) => prev.filter((r) => r.id !== id));
    setMonthlyPricesOpen((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleRoomChange = (id, key, value) => {
    setRooms((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
  };

  const handleRoomMonthlyPriceChange = (id, monthKey, value) => {
    setRooms((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        return {
          ...r,
          monthlyPrices: {
            ...createEmptyMonthlyPrices(),
            ...(r.monthlyPrices || {}),
            [monthKey]: value === "" ? "" : Number(value),
          },
        };
      })
    );
  };

  const handleRoomTypeChange = (id, type) => {
    if (!isRoomsMode) return;
    setRooms((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const fallback = DEFAULT_PRICE_BY_TYPE[type] ?? 0;
        const current = Number(r.price || 0);
        return { ...r, type, price: current > 0 ? current : fallback };
      })
    );
  };

  const handleImageChange = (e) => {
    const files = [...(e.target.files || [])];
    const newUrls = files.map((f) => URL.createObjectURL(f));
    setImages((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...newUrls]);
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setPreviewUrls((prev) => {
      const toRemove = prev[idx];
      try {
        if (toRemove) URL.revokeObjectURL(toRemove);
      } catch {}
      return prev.filter((_, i) => i !== idx);
    });
  };

  const setCoverPhoto = (idx) => {
    if (idx <= 0) return;
    setImages((prev) => {
      const arr = [...prev];
      const picked = arr.splice(idx, 1)[0];
      arr.unshift(picked);
      return arr;
    });
    setPreviewUrls((prev) => {
      const arr = [...prev];
      const picked = arr.splice(idx, 1)[0];
      arr.unshift(picked);
      return arr;
    });
  };

  useEffect(() => {
    return () => {
      try {
        previewUrls.forEach((u) => u && URL.revokeObjectURL(u));
      } catch {}
      try {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      } catch {}
      try {
        if (abortRef.current) abortRef.current.abort();
      } catch {}
    };
  }, [previewUrls]);

  const doSearch = async (q) => {
    const s = String(q || "").trim();
    if (!s) {
      setLocResults([]);
      setLocError("");
      setLocLoading(false);
      return;
    }

    if (lastQueryRef.current === s) return;

    lastQueryRef.current = s;

    if (s.length < 2) {
      setLocResults([]);
      setLocError("");
      setLocLoading(false);
      return;
    }

    const cached = locCacheRef.current.get(s);
    if (cached) {
      setLocResults(cached);
      setLocError(cached.length ? "" : t("addProperty.errors.noResults", { defaultValue: "No results." }));
      setLocLoading(false);
      return;
    }

    try {
      if (abortRef.current) abortRef.current.abort();
    } catch {}

    const controller = new AbortController();
    abortRef.current = controller;

    setLocLoading(true);
    setLocError("");

    try {
      const results = await searchGR(s, controller.signal);
      if (lastQueryRef.current !== s) return;

      setLocResults(results);

      try {
        const m = locCacheRef.current;
        m.set(s, results);
        if (m.size > 20) {
          const firstKey = m.keys().next().value;
          m.delete(firstKey);
        }
      } catch {}

      if (!results || results.length === 0) {
        setLocError(t("addProperty.errors.noResults", { defaultValue: "No results." }));
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error(e);
      if (lastQueryRef.current !== s) return;

      setLocResults([]);
      setLocError(t("addProperty.errors.searchFailed", { defaultValue: "Search failed. Try again." }));
    } finally {
      if (lastQueryRef.current === s) setLocLoading(false);
    }
  };

  const onLocInput = (val) => {
    const v = String(val || "");
    setLocQuery(v);
    setLocOpen(true);
    setLocError("");

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 850);
  };

  const pickSearchResult = (item) => {
    const lat = Number(item.lat);
    const lng = Number(item.lon);
    const text = item.display_name;
    const obj = { text, lat, lng, address: item.address || {}, countryCode: "GR" };

    setLocationObj(obj);
    setLocQuery(text);
    setLocOpen(false);
    setLocResults([]);
    setLocError("");
  };

  const pickFromMap = async (latlng) => {
    const lat = Number(latlng.lat);
    const lng = Number(latlng.lng);

    try {
      const rev = await reverseGeocodeGR(lat, lng);
      if (!rev) {
        alert(t("addProperty.errors.grOnly", { defaultValue: "Please pick a location in Greece 🇬🇷" }));
        return;
      }
      const text = rev.display_name || `(${lat.toFixed(5)}, ${lng.toFixed(5)})`;
      const obj = { text, lat, lng, address: rev.address || {}, countryCode: "GR" };

      setLocationObj(obj);
      setLocQuery(text);
      setLocOpen(false);
      setLocResults([]);
      setLocError("");
    } catch (e) {
      console.error(e);
      alert(t("addProperty.errors.revError", { defaultValue: "Reverse geocoding failed. Try again." }));
    }
  };

  const clearLocation = () => {
    setLocationObj(null);
    setLocQuery("");
    setLocResults([]);
    setLocOpen(false);
    setLocError("");
  };

  const mapCenter = useMemo(() => {
    if (locationObj?.lat && locationObj?.lng) return [locationObj.lat, locationObj.lng];
    return [37.9838, 23.7275];
  }, [locationObj]);

  /* ===================== Draft autosave ===================== */
  const draftTimerRef = useRef(null);
  const loadedDraftOnceRef = useRef(false);

  const buildDraft = () => ({
    step: Number(step || 0),
    title,
    description,

    wifi: !!wifi,
    parking: !!parking,
    pool: !!pool,

    propertyType: String(propertyType || "apartment"),
    maxGuests: Number(maxGuests || 0),
    bedrooms: Number(bedrooms || 0),
    bathrooms: Number(bathrooms || 0),

    amenitiesExtra: { ...(amenitiesExtra || {}) },
    houseRules: { ...(houseRules || {}) },
    fees: { ...(fees || {}) },
    discounts: { ...(discounts || {}) },

    locationObj: locationObj
      ? {
          text: String(locationObj.text || ""),
          lat: Number(locationObj.lat),
          lng: Number(locationObj.lng),
          address: locationObj.address || {},
          countryCode: "GR",
        }
      : null,
    locQuery: String(locQuery || ""),

    rooms: serializeRoomsForDraft(rooms),

    _meta: { v: 1, savedAt: Date.now() },
  });

  const saveDraft = () => {
    try {
      const payload = buildDraft();
      localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch {}
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
  };

  useEffect(() => {
    if (loadedDraftOnceRef.current) return;
    loadedDraftOnceRef.current = true;

    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = safeJsonParse(raw);
      if (!draft || typeof draft !== "object") return;

      const looksEmpty =
        !title &&
        !description &&
        !locationObj?.text &&
        (!Array.isArray(rooms) || rooms.length === 0 || !(rooms?.[0]?.availableDates?.length > 0)) &&
        previewUrls.length === 0;

      const shouldAsk = looksEmpty;
      const ok = shouldAsk
        ? window.confirm(
            t("addProperty.draft.restoreConfirm", {
              defaultValue:
                "You have a saved draft for this listing. Restore it now?\n\nNote: photos are not saved and must be added again.",
            })
          )
        : false;

      if (!ok) return;

      setStep(clamp(toInt(draft.step, 0), 0, STEPS.length - 1));

      setTitle(String(draft.title || ""));
      setDescription(String(draft.description || ""));

      setWifi(!!draft.wifi);
      setParking(!!draft.parking);
      setPool(!!draft.pool);

      const safeType = ["apartment", "hotel", "villa", "room"].includes(String(draft.propertyType))
        ? String(draft.propertyType)
        : "apartment";
      setPropertyType(safeType);

      setMaxGuests(toInt(draft.maxGuests, 2));
      setBedrooms(toInt(draft.bedrooms, 1));
      setBathrooms(toInt(draft.bathrooms, 1));

      if (draft.amenitiesExtra && typeof draft.amenitiesExtra === "object") setAmenitiesExtra((p) => ({ ...p, ...draft.amenitiesExtra }));
      if (draft.houseRules && typeof draft.houseRules === "object") setHouseRules((p) => ({ ...p, ...draft.houseRules }));
      if (draft.fees && typeof draft.fees === "object") setFees((p) => ({ ...p, ...draft.fees }));
      if (draft.discounts && typeof draft.discounts === "object") setDiscounts((p) => ({ ...p, ...draft.discounts }));

      if (draft.locationObj && typeof draft.locationObj === "object") {
        const lo = draft.locationObj;
        const obj = {
          text: String(lo.text || ""),
          lat: Number(lo.lat),
          lng: Number(lo.lng),
          address: lo.address || {},
          countryCode: "GR",
        };
        if (obj.text && Number.isFinite(obj.lat) && Number.isFinite(obj.lng)) {
          setLocationObj(obj);
          setLocQuery(String(draft.locQuery || obj.text));
        }
      } else {
        setLocQuery(String(draft.locQuery || ""));
      }

      const hydratedRooms = hydrateRoomsFromDraft(draft.rooms);
      if (hydratedRooms.length) setRooms(hydratedRooms);
    } catch {}
  }, [description, locationObj, previewUrls.length, rooms, t, title]);

  useEffect(() => {
    try {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => saveDraft(), 700);
    } catch {}
    return () => {
      try {
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      } catch {}
    };
  }, [
    step,
    title,
    description,
    wifi,
    parking,
    pool,
    propertyType,
    maxGuests,
    bedrooms,
    bathrooms,
    amenitiesExtra,
    houseRules,
    fees,
    discounts,
    locationObj,
    locQuery,
    rooms,
  ]);

  const handleAddProperty = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!user) return alert(t("addProperty.errors.loginFirst", { defaultValue: "Please log in first." }));
    if (!title || !locationObj?.text)
      return alert(t("addProperty.errors.fillRequired", { defaultValue: "Please fill required fields." }));
    if (rooms.some((r) => !r.availableDates || r.availableDates.length === 0))
      return alert(t("addProperty.errors.selectDatesAllRooms", { defaultValue: "Select available dates." }));
    if (rooms.some((r) => Number(r.price || 0) <= 0))
      return alert(t("addProperty.errors.setValidPriceAllRooms", { defaultValue: "Set a valid price." }));
    if (images.length === 0)
      return alert(t("addProperty.errors.selectAtLeastOneImage", { defaultValue: "Please add at least one image." }));

    const formattedRooms = rooms.map((r) => ({
      ...r,
      price: Number(r.price || 0),
      monthlyPrices: sanitizeMonthlyPrices(r.monthlyPrices),
      availableDates: (r.availableDates || []).map(toISO).filter(Boolean),
      reservedDates: Array.isArray(r.reservedDates) ? r.reservedDates : [],
    }));

    const amenities = {
      wifi: !!wifi,
      parking: !!parking,
      pool: !!pool,
      ...amenitiesExtra,
    };

    const safeMaxGuests = clamp(toInt(maxGuests, 0), 0, 100);
    const safeBedrooms = clamp(toInt(bedrooms, 0), 0, 50);
    const safeBathrooms = clamp(toInt(bathrooms, 0), 0, 50);

    const safeCleaningFee = clamp(Number(fees?.cleaningFee || 0), 0, 1_000_000);
    const safeWeekly = clamp(Number(discounts?.weeklyPercent || 0), 0, 100);
    const safeMonthly = clamp(Number(discounts?.monthlyPercent || 0), 0, 100);

    const safeHouseRules = {
      petsAllowed: !!houseRules?.petsAllowed,
      smokingAllowed: !!houseRules?.smokingAllowed,
      checkInFrom: normalizeTime(houseRules?.checkInFrom, "15:00"),
      checkInTo: normalizeTime(houseRules?.checkInTo, "22:00"),
      checkOutUntil: normalizeTime(houseRules?.checkOutUntil, "11:00"),
    };

    const safeType = ["apartment", "hotel", "villa", "room"].includes(String(propertyType))
      ? String(propertyType)
      : "apartment";

    const formData = new FormData();
    formData.append("title", title);

    formData.append("location", locationObj.text);
    formData.append("countryCode", "GR");
    formData.append("lat", String(locationObj.lat));
    formData.append("lng", String(locationObj.lng));
    formData.append("address", JSON.stringify(locationObj.address || {}));

    formData.append("price", String(basePrice || 0));
    formData.append("description", description);

    formData.append("wifi", wifi ? "true" : "false");
    formData.append("parking", parking ? "true" : "false");
    formData.append("pool", pool ? "true" : "false");

    formData.append("propertyType", safeType);
    formData.append("maxGuests", String(safeMaxGuests));
    formData.append("bedrooms", String(safeBedrooms));
    formData.append("bathrooms", String(safeBathrooms));

    formData.append("amenities", JSON.stringify(amenities));
    formData.append("houseRules", JSON.stringify(safeHouseRules));
    formData.append("fees", JSON.stringify({ cleaningFee: safeCleaningFee }));
    formData.append("discounts", JSON.stringify({ weeklyPercent: safeWeekly, monthlyPercent: safeMonthly }));

    formData.append("rooms", JSON.stringify(formattedRooms));
    images.forEach((img) => formData.append("images", img));

    try {
      setSubmitting(true);

      const res = await fetch(`${API_URL}/api/properties`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        data = { message: text };
      }

      if (!res.ok) {
        alert(data?.error || data?.message || `Failed to add property (${res.status})`);
        return;
      }

      const createdRaw = data?.property || data;
      if (!createdRaw) {
        alert("Failed to add property. Missing payload from server.");
        return;
      }

      const created = normalizeProperty(createdRaw);
      if (typeof addProperty === "function") addProperty(created);

      setStep(0);
      setTitle("");
      clearLocation();
      setDescription("");

      setWifi(false);
      setParking(false);
      setPool(false);

      setPropertyType("apartment");
      setMaxGuests(2);
      setBedrooms(1);
      setBathrooms(1);

      setAmenitiesExtra({
        ac: false,
        kitchen: false,
        tv: false,
        washer: false,
        heating: false,
        elevator: false,
        seaView: false,
      });

      setHouseRules({
        petsAllowed: false,
        smokingAllowed: false,
        checkInFrom: "15:00",
        checkInTo: "22:00",
        checkOutUntil: "11:00",
      });

      setFees({ cleaningFee: 0 });
      setDiscounts({ weeklyPercent: 0, monthlyPercent: 0 });

      setRooms([
        {
          id: Date.now(),
          type: "Single",
          price: DEFAULT_PRICE_BY_TYPE.Single,
          monthlyPrices: createEmptyMonthlyPrices(),
          availableDates: [],
          reservedDates: [],
        },
      ]);

      setMonthlyPricesOpen({});

      try {
        previewUrls.forEach((u) => u && URL.revokeObjectURL(u));
      } catch {}

      setImages([]);
      setPreviewUrls([]);

      clearDraft();

      navigate("/my-properties");
    } catch (err) {
      console.error("Error adding property:", err);
      alert("Failed to add property. Check console.");
    } finally {
      setSubmitting(false);
    }
  };

  const propertyTypeLabel = useMemo(() => {
    const v = String(propertyType || "apartment");
    if (v === "hotel") return "Hotel";
    if (v === "villa") return "Villa";
    if (v === "room") return "Room";
    return "Apartment";
  }, [propertyType]);

  const stepOk = useMemo(() => {
    const hasTitle = !!String(title || "").trim();
    const hasLocation = !!locationObj?.text;
    const hasPhotos = images.length > 0;
    const roomsOk =
      Array.isArray(rooms) &&
      rooms.length > 0 &&
      rooms.every((r) => Array.isArray(r.availableDates) && r.availableDates.length > 0 && Number(r.price || 0) > 0);

    if (step === 0) return hasTitle && Number(maxGuests || 0) >= 0;
    if (step === 1) return hasLocation;
    if (step === 2) return true;
    if (step === 3) return hasPhotos && roomsOk;
    return false;
  }, [step, title, maxGuests, locationObj, images.length, rooms]);

  const goStep = (n) => setStep(clamp(toInt(n, 0), 0, STEPS.length - 1));

  const explainStepMissing = () => {
    if (step === 0) {
      if (!String(title || "").trim()) {
        return t("addProperty.errors.needTitle", { defaultValue: "Please add a title to continue." });
      }
      return t("addProperty.errors.fixBasics", { defaultValue: "Please review the basics." });
    }
    if (step === 1) {
      return t("addProperty.errors.needLocation", { defaultValue: "Please select a location in Greece to continue." });
    }
    if (step === 3) {
      if (images.length === 0) {
        return t("addProperty.errors.selectAtLeastOneImage", { defaultValue: "Please add at least one image." });
      }
      return t("addProperty.errors.selectDatesAllRooms", { defaultValue: "Please set price and available dates." });
    }
    return t("addProperty.errors.fillRequired", { defaultValue: "Please fill required fields." });
  };

  const handleNext = () => {
    if (!stepOk) return alert(explainStepMissing());
    goStep(step + 1);
    try {
      formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    } catch {}
  };

  const handleBack = () => {
    goStep(step - 1);
    try {
      formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    } catch {}
  };

  const checklist = useMemo(() => {
    const hasTitle = !!String(title || "").trim();
    const hasLocation = !!locationObj?.text;
    const hasPhotos = images.length > 0;
    const roomsOk =
      Array.isArray(rooms) &&
      rooms.length > 0 &&
      rooms.every((r) => Array.isArray(r.availableDates) && r.availableDates.length > 0 && Number(r.price || 0) > 0);
    return { hasTitle, hasLocation, hasPhotos, roomsOk };
  }, [title, locationObj, images.length, rooms]);

  return (
    <div className="addp">
      <div className="ap-shell">
        <form
          ref={formRef}
          onSubmit={handleAddProperty}
          className={`ap-shell ${isNarrow ? "ap-shell--mobile" : ""}`}
        >
          <div className="ap-grid">
            {/* LEFT */}
            <div className="ap-card">
              <div className="ap-cardHeader">
                <h2 className="ap-title">{t("addProperty.title")}</h2>
                <div className="ap-subtitle">{t("addProperty.subtitle")}</div>
              </div>

              <div className="ap-cardBody">
                {step === 0 && (
                  <>
                    <div className="ap-sectionTitle">{t("addProperty.section.title")}</div>
                    <input
                      className="ap-input"
                      type="text"
                      placeholder={t("addProperty.placeholders.title")}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />

                    <div className="ap-sectionTitle">{t("addProperty.section.basics", { defaultValue: "Basics" })}</div>

                    <div className="ap-row2">
                      <div>
                        <div className="ap-miniLabel">
                          {t("addProperty.labels.propertyType", { defaultValue: "Property type" })}
                        </div>
                        <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} className="ap-select">
                          <option value="apartment">{t("addProperty.propertyType.apartment", { defaultValue: "Apartment" })}</option>
                          <option value="hotel">{t("addProperty.propertyType.hotel", { defaultValue: "Hotel" })}</option>
                          <option value="villa">{t("addProperty.propertyType.villa", { defaultValue: "Villa" })}</option>
                          <option value="room">{t("addProperty.propertyType.room", { defaultValue: "Room" })}</option>
                        </select>
                      </div>

                      <div>
                        <div className="ap-miniLabel">{t("addProperty.labels.maxGuests", { defaultValue: "Max guests" })}</div>
                        <input
                          className="ap-input"
                          type="number"
                          min={0}
                          value={maxGuests}
                          onChange={(e) => setMaxGuests(toInt(e.target.value, 0))}
                          placeholder={t("addProperty.placeholders.maxGuests", { defaultValue: "e.g. 2" })}
                        />
                      </div>
                    </div>

                    {!isRoomsMode && (
                      <div className="ap-row2">
                        <div>
                          <div className="ap-miniLabel">{t("addProperty.labels.bedrooms", { defaultValue: "Bedrooms" })}</div>
                          <input
                            className="ap-input"
                            type="number"
                            min={0}
                            value={bedrooms}
                            onChange={(e) => setBedrooms(toInt(e.target.value, 0))}
                            placeholder={t("addProperty.placeholders.bedrooms", { defaultValue: "e.g. 1" })}
                          />
                        </div>

                        <div>
                          <div className="ap-miniLabel">{t("addProperty.labels.bathrooms", { defaultValue: "Bathrooms" })}</div>
                          <input
                            className="ap-input"
                            type="number"
                            min={0}
                            value={bathrooms}
                            onChange={(e) => setBathrooms(toInt(e.target.value, 0))}
                            placeholder={t("addProperty.placeholders.bathrooms", { defaultValue: "e.g. 1" })}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {step === 1 && (
                  <>
                    <div className="ap-sectionTitle">{t("addProperty.section.location")}</div>
                    <div className="ap-locWrap">
                      <input
                        className="ap-input ap-input--withClear"
                        type="text"
                        placeholder={t("addProperty.placeholders.location")}
                        value={locQuery}
                        onChange={(e) => onLocInput(e.target.value)}
                        onFocus={() => setLocOpen(true)}
                      />
                      {locQuery && (
                        <button type="button" onClick={clearLocation} className="ap-clearBtn" title="Clear">
                          <X size={16} strokeWidth={2} />
                        </button>
                      )}

                      {locOpen && (locLoading || locResults.length > 0 || !!locError) && (
                        <div className="ap-drop">
                          {locLoading && <div className="ap-dropLoading">Searching…</div>}

                          {!locLoading &&
                            locResults.map((r, idx) => (
                              <button
                                key={`${r.place_id}-${idx}`}
                                type="button"
                                onClick={() => pickSearchResult(r)}
                                className={`ap-dropItem ${idx === 0 ? "ap-dropItem--first" : ""}`}
                              >
                                <div className="ap-dropTitle">
                                  {r.address?.city ||
                                    r.address?.town ||
                                    r.address?.village ||
                                    r.address?.suburb ||
                                    r.address?.neighbourhood ||
                                    "Result"}
                                </div>
                                <div className="ap-dropSub">{r.display_name}</div>
                              </button>
                            ))}

                          {!locLoading && locResults.length === 0 && !!locError && <div className="ap-dropLoading">{locError}</div>}
                        </div>
                      )}
                    </div>

                    <div className="ap-mapWrap">
                      <MapContainer center={mapCenter} zoom={locationObj?.lat ? 13 : 6} className="ap-map">
                        <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <FlyTo center={mapCenter} zoom={locationObj?.lat ? 13 : 6} />
                        <ClickToSetMarker onPick={pickFromMap} />
                        {locationObj?.lat && locationObj?.lng && <Marker position={[locationObj.lat, locationObj.lng]} />}
                      </MapContainer>
                      <div className="ap-mapHint">{t("addProperty.tips.map")}</div>
                    </div>

                    {locationObj?.text && (
                      <div className="ap-selectedLoc">
                        {t("addProperty.labels.selected")}: <span className="ap-selectedLocText">{locationObj.text}</span>
                      </div>
                    )}
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="ap-sectionTitle">{t("addProperty.section.description")}</div>
                    <textarea
                      className="ap-textarea"
                      placeholder={t("addProperty.placeholders.description")}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                    />

                    <div className="ap-sectionTitle">{t("addProperty.section.amenities")}</div>

                    <div className="ap-chips">
                      <div className={`ap-chip ${wifi ? "ap-chipActive" : ""}`} onClick={() => setWifi((v) => !v)}>
                        <ChipIcon Icon={Wifi} /> {t("addProperty.amenities.wifi")}
                      </div>
                      <div className={`ap-chip ${parking ? "ap-chipActive" : ""}`} onClick={() => setParking((v) => !v)}>
                        <ChipIcon Icon={ParkingSquare} /> {t("addProperty.amenities.parking")}
                      </div>
                      <div className={`ap-chip ${pool ? "ap-chipActive" : ""}`} onClick={() => setPool((v) => !v)}>
                        <ChipIcon Icon={Waves} /> {t("addProperty.amenities.pool")}
                      </div>
                    </div>

                    <div className="ap-chips ap-chips--extra">
                      <div className={`ap-chip ${amenitiesExtra.ac ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("ac")}>
                        <ChipIcon Icon={Snowflake} /> {t("addProperty.amenities.ac", { defaultValue: "AC" })}
                      </div>
                      <div className={`ap-chip ${amenitiesExtra.kitchen ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("kitchen")}>
                        <ChipIcon Icon={CookingPot} /> {t("addProperty.amenities.kitchen", { defaultValue: "Kitchen" })}
                      </div>
                      <div className={`ap-chip ${amenitiesExtra.tv ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("tv")}>
                        <ChipIcon Icon={Tv} /> {t("addProperty.amenities.tv", { defaultValue: "TV" })}
                      </div>
                      <div className={`ap-chip ${amenitiesExtra.washer ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("washer")}>
                        <ChipIcon Icon={WashingMachine} /> {t("addProperty.amenities.washer", { defaultValue: "Washer" })}
                      </div>
                      <div className={`ap-chip ${amenitiesExtra.heating ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("heating")}>
                        <ChipIcon Icon={Flame} /> {t("addProperty.amenities.heating", { defaultValue: "Heating" })}
                      </div>
                      <div className={`ap-chip ${amenitiesExtra.elevator ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("elevator")}>
                        <ChipIcon Icon={Building2} /> {t("addProperty.amenities.elevator", { defaultValue: "Elevator" })}
                      </div>
                      <div className={`ap-chip ${amenitiesExtra.seaView ? "ap-chipActive" : ""}`} onClick={() => toggleAmenity("seaView")}>
                        <ChipIcon Icon={Waves} /> {t("addProperty.amenities.seaView", { defaultValue: "Sea view" })}
                      </div>
                    </div>

                    <div className="ap-sectionTitle">{t("addProperty.section.houseRules", { defaultValue: "House rules" })}</div>

                    <div className="ap-chips">
                      <div
                        className={`ap-chip ${houseRules.petsAllowed ? "ap-chipActive" : ""}`}
                        onClick={() => setHouseRules((p) => ({ ...p, petsAllowed: !p.petsAllowed }))}
                      >
                        <ChipIcon Icon={PawPrint} /> {t("addProperty.houseRules.pets", { defaultValue: "Pets allowed" })}
                      </div>

                      <div
                        className={`ap-chip ${houseRules.smokingAllowed ? "ap-chipActive" : ""}`}
                        onClick={() => setHouseRules((p) => ({ ...p, smokingAllowed: !p.smokingAllowed }))}
                      >
                        <ChipIcon Icon={CigaretteOff} /> {t("addProperty.houseRules.smoking", { defaultValue: "Smoking allowed" })}
                      </div>
                    </div>

                    <div className="ap-row2 ap-row2--mt">
                      <div>
                        <div className="ap-miniLabel">{t("addProperty.houseRules.checkInFrom", { defaultValue: "Check-in from" })}</div>
                        <input
                          className="ap-input"
                          type="time"
                          value={houseRules.checkInFrom}
                          onChange={(e) => setHouseRules((p) => ({ ...p, checkInFrom: e.target.value }))}
                        />
                      </div>

                      <div>
                        <div className="ap-miniLabel">{t("addProperty.houseRules.checkInTo", { defaultValue: "Check-in to" })}</div>
                        <input
                          className="ap-input"
                          type="time"
                          value={houseRules.checkInTo}
                          onChange={(e) => setHouseRules((p) => ({ ...p, checkInTo: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="ap-mt10">
                      <div className="ap-miniLabel">{t("addProperty.houseRules.checkOutUntil", { defaultValue: "Check-out until" })}</div>
                      <input
                        className="ap-input"
                        type="time"
                        value={houseRules.checkOutUntil}
                        onChange={(e) => setHouseRules((p) => ({ ...p, checkOutUntil: e.target.value }))}
                      />
                    </div>

                    <div className="ap-sectionTitle">{t("addProperty.section.pricingExtras", { defaultValue: "Pricing extras" })}</div>

                    <div className="ap-row2">
                      <div>
                        <div className="ap-miniLabel">{t("addProperty.pricing.cleaningFee", { defaultValue: "Cleaning fee (€)" })}</div>
                        <input
                          className="ap-input"
                          type="number"
                          min={0}
                          value={fees.cleaningFee}
                          onChange={(e) => setFees((p) => ({ ...p, cleaningFee: Number(e.target.value) }))}
                          placeholder="0"
                        />
                      </div>

                      <div>
                        <div className="ap-miniLabel">{t("addProperty.pricing.weeklyDiscount", { defaultValue: "Weekly discount (%)" })}</div>
                        <input
                          className="ap-input"
                          type="number"
                          min={0}
                          max={100}
                          value={discounts.weeklyPercent}
                          onChange={(e) => setDiscounts((p) => ({ ...p, weeklyPercent: Number(e.target.value) }))}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div className="ap-mt10">
                      <div className="ap-miniLabel">{t("addProperty.pricing.monthlyDiscount", { defaultValue: "Monthly discount (%)" })}</div>
                      <input
                        className="ap-input"
                        type="number"
                        min={0}
                        max={100}
                        value={discounts.monthlyPercent}
                        onChange={(e) => setDiscounts((p) => ({ ...p, monthlyPercent: Number(e.target.value) }))}
                        placeholder="0"
                      />
                    </div>
                  </>
                )}

                {step === 3 && (
                  <>
                    <div className="ap-sectionTitle ap-sectionTitle--rooms">
                      {isRoomsMode ? t("addProperty.section.rooms") : t("addProperty.section.availability", { defaultValue: "Availability & price" })}
                    </div>

                    {!isRoomsMode && rooms[0] && (
                      <div className="ap-roomCard">
                        <div className="ap-row2">
                          <div>
                            <div className="ap-miniLabel">{t("addProperty.labels.pricePerNight")}</div>
                            <input
                              className="ap-input"
                              type="number"
                              value={rooms[0].price ?? 0}
                              min={0}
                              onChange={(e) => handleRoomChange(rooms[0].id, "price", Number(e.target.value))}
                              placeholder={t("addProperty.placeholders.price")}
                            />
                          </div>
                          <div>
                            <div className="ap-miniLabel">{t("addProperty.labels.type", { defaultValue: "Type" })}</div>
                            <input className="ap-input" value={propertyTypeLabel} readOnly />
                          </div>
                        </div>

                        <div
                          style={{
                            marginTop: 14,
                            padding: 14,
                            borderRadius: 14,
                            border: "1px solid rgba(0,0,0,0.08)",
                            background: "rgba(0,0,0,0.02)",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleMonthlyPricesPanel(rooms[0].id)}
                            className="ap-ctaBtn"
                            style={{
                              width: "100%",
                              justifyContent: "center",
                              background: "white",
                              color: "#222",
                              border: "1px solid rgba(0,0,0,0.10)",
                              boxShadow: "none",
                            }}
                          >
                            {monthlyPricesOpen[rooms[0].id]
                              ? t("addProperty.labels.hideMonthlyPrices", { defaultValue: "Hide monthly prices" })
                              : t("addProperty.labels.showMonthlyPrices", { defaultValue: "Show monthly prices" })}
                          </button>

                          {monthlyPricesOpen[rooms[0].id] && (
                            <>
                              <div className="ap-miniLabel" style={{ marginTop: 12, marginBottom: 8 }}>
                                {t("addProperty.labels.monthlyPrices", { defaultValue: "Monthly prices (optional)" })}
                              </div>
                              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                                {t("addProperty.labels.monthlyPricesHint", {
                                  defaultValue: "Leave a month empty to use the standard nightly price.",
                                })}
                              </div>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                                  gap: 10,
                                }}
                              >
                                {MONTHS.map((month) => (
                                  <div key={month.key}>
                                    <div className="ap-miniLabel" style={{ marginBottom: 6 }}>
                                      {month.short}
                                    </div>
                                    <input
                                      className="ap-input"
                                      type="number"
                                      min={0}
                                      value={rooms[0]?.monthlyPrices?.[month.key] ?? ""}
                                      onChange={(e) => handleRoomMonthlyPriceChange(rooms[0].id, month.key, e.target.value)}
                                      placeholder={String(rooms[0].price || 0)}
                                    />
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>

                        <div className="ap-dates">
                          <div className="ap-datesTop">
                            <div className="ap-miniLabel">{t("addProperty.labels.availableDates")}</div>
                            <div className="ap-help">{t("addProperty.labels.pickMultiple")}</div>
                          </div>

                          <div className="ap-datePickerWrap">
                            <DatePicker
                              multiple
                              value={rooms[0].availableDates || []}
                              onChange={(dates) => handleRoomChange(rooms[0].id, "availableDates", dates || [])}
                              format="YYYY/MM/DD"
                              className="teal"
                              sort
                              monthsShown={2}
                              calendarPosition="bottom-center"
                              portal
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {isRoomsMode &&
                      rooms.map((room, idx) => (
                        <div key={room.id} className="ap-roomCard">
                          <div className="ap-roomHeader">
                            <div className="ap-roomBadge">
                              <span className="ap-roomIndex">{idx + 1}</span>
                              <span>
                                {t("addProperty.labels.room")} {idx + 1}
                              </span>
                            </div>

                            {rooms.length > 1 && (
                              <button type="button" className="ap-dangerLink" onClick={() => handleRemoveRoom(room.id)}>
                                {t("addProperty.labels.remove")}
                              </button>
                            )}
                          </div>

                          <div className="ap-row2">
                            <div>
                              <div className="ap-miniLabel">{t("addProperty.labels.roomType")}</div>
                              <select value={room.type} onChange={(e) => handleRoomTypeChange(room.id, e.target.value)} className="ap-select">
                                <option value="Single">Single</option>
                                <option value="Double">Double</option>
                                <option value="Triple">Triple</option>
                              </select>
                            </div>

                            <div>
                              <div className="ap-miniLabel">{t("addProperty.labels.pricePerNight")}</div>
                              <input
                                className="ap-input"
                                type="number"
                                value={room.price ?? 0}
                                min={0}
                                onChange={(e) => handleRoomChange(room.id, "price", Number(e.target.value))}
                                placeholder={t("addProperty.placeholders.price")}
                              />
                            </div>
                          </div>

                          <div
                            style={{
                              marginTop: 14,
                              padding: 14,
                              borderRadius: 14,
                              border: "1px solid rgba(0,0,0,0.08)",
                              background: "rgba(0,0,0,0.02)",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => toggleMonthlyPricesPanel(room.id)}
                              className="ap-ctaBtn"
                              style={{
                                width: "100%",
                                justifyContent: "center",
                                background: "white",
                                color: "#222",
                                border: "1px solid rgba(0,0,0,0.10)",
                                boxShadow: "none",
                              }}
                            >
                              {monthlyPricesOpen[room.id]
                                ? t("addProperty.labels.hideMonthlyPrices", { defaultValue: "Hide monthly prices" })
                                : t("addProperty.labels.showMonthlyPrices", { defaultValue: "Show monthly prices" })}
                            </button>

                            {monthlyPricesOpen[room.id] && (
                              <>
                                <div className="ap-miniLabel" style={{ marginTop: 12, marginBottom: 8 }}>
                                  {t("addProperty.labels.monthlyPrices", { defaultValue: "Monthly prices (optional)" })}
                                </div>
                                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
                                  {t("addProperty.labels.monthlyPricesHint", {
                                    defaultValue: "Leave a month empty to use the standard nightly price.",
                                  })}
                                </div>

                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                                    gap: 10,
                                  }}
                                >
                                  {MONTHS.map((month) => (
                                    <div key={month.key}>
                                      <div className="ap-miniLabel" style={{ marginBottom: 6 }}>
                                        {month.short}
                                      </div>
                                      <input
                                        className="ap-input"
                                        type="number"
                                        min={0}
                                        value={room?.monthlyPrices?.[month.key] ?? ""}
                                        onChange={(e) => handleRoomMonthlyPriceChange(room.id, month.key, e.target.value)}
                                        placeholder={String(room.price || 0)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>

                          <div className="ap-dates">
                            <div className="ap-datesTop">
                              <div className="ap-miniLabel">{t("addProperty.labels.availableDates")}</div>
                              <div className="ap-help">{t("addProperty.labels.pickMultiple")}</div>
                            </div>

                            <div className="ap-datePickerWrap">
                              <DatePicker
                                multiple
                                value={room.availableDates || []}
                                onChange={(dates) => handleRoomChange(room.id, "availableDates", dates || [])}
                                format="YYYY/MM/DD"
                                className="teal"
                                sort
                                monthsShown={2}
                                calendarPosition="bottom-center"
                                portal
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                    {isRoomsMode && (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={handleAddRoom}
                        onKeyDown={(e) => e.key === "Enter" && handleAddRoom()}
                        className="ap-addTile"
                      >
                        <span className="ap-plus">
                          <Plus size={18} strokeWidth={2} />
                        </span>
                        <span>{t("addProperty.labels.addAnotherRoom")}</span>
                      </div>
                    )}

                    <div className="ap-sectionTitle">{t("addProperty.section.photos")}</div>

                    <div className="ap-upload">
                      <div className="ap-uploadTop">
                        <p className="ap-uploadTitle">{t("addProperty.labels.addPhotos")}</p>

                        <label className="ap-uploadBtn">
                          <Plus className="ap-ico ap-ico--inline" size={16} strokeWidth={2} /> {t("addProperty.labels.addPhotos")}
                          <input type="file" name="images" multiple onChange={handleImageChange} accept="image/*" className="ap-fileInput" />
                        </label>
                      </div>

                      {previewUrls.length > 0 && (
                        <div className="ap-photoGrid">
                          {previewUrls.map((img, idx) => (
                            <div key={idx} className="ap-photoItem">
                              <img src={img} alt="preview" />

                              <div className="ap-photoActions">
                                <button
                                  type="button"
                                  onClick={() => setCoverPhoto(idx)}
                                  className="ap-iconBtn"
                                  title={t("addProperty.labels.setCover", { defaultValue: "Set as cover" })}
                                  disabled={idx === 0}
                                  style={{ marginRight: 8, opacity: idx === 0 ? 0.5 : 1 }}
                                >
                                  <ImageIcon size={16} strokeWidth={2} />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => removeImage(idx)}
                                  className="ap-iconBtn"
                                  title={t("addProperty.labels.remove")}
                                >
                                  <X size={16} strokeWidth={2} />
                                </button>
                              </div>

                              {idx === 0 && (
                                <div
                                  className="ap-coverBadge"
                                  style={{
                                    position: "absolute",
                                    top: 10,
                                    left: 10,
                                    padding: "4px 8px",
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 800,
                                    background: "rgba(255,255,255,0.92)",
                                    border: "1px solid rgba(0,0,0,0.10)",
                                  }}
                                >
                                  {t("addProperty.labels.cover", { defaultValue: "Cover" })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

                <button type="submit" className="ap-hiddenSubmit" />
              </div>
            </div>

            {/* RIGHT */}
            <div className="ap-card ap-card--aside">
              <div className="ap-cardHeader">
                <h3 className="ap-asideTitle">{t("addProperty.section.previewTitle")}</h3>
                <div className="ap-subtitle">{t("addProperty.section.previewSubtitle")}</div>
              </div>

              <div className="ap-cardBody">
                <div className="ap-previewTop">
                  <div className="ap-previewKicker">{t("addProperty.labels.startingFrom")}</div>

                  <div className="ap-previewPrice">
                    <span className="ap-big">€{basePrice || 0}</span>
                    <span className="ap-per">{t("addProperty.labels.perNight")}</span>
                  </div>

                  <div className="ap-previewTitle">{title || t("addProperty.preview.untitled")}</div>

                  <p className="ap-previewMeta">
                    <InlineIcon Icon={MapPin} /> {locationObj?.text ? locationObj.text : t("addProperty.preview.pickGreece")}
                  </p>

                  <p className="ap-previewMeta">
                    <InlineIcon Icon={Tag} /> <b>{propertyTypeLabel}</b> • <InlineIcon Icon={Users} /> <b>{toInt(maxGuests, 0)}</b>
                    {!isRoomsMode && (
                      <>
                        {" "}
                        • <InlineIcon Icon={BedDouble} /> <b>{toInt(bedrooms, 0)}</b> • <InlineIcon Icon={Bath} />{" "}
                        <b>{toInt(bathrooms, 0)}</b>
                      </>
                    )}
                  </p>

                  {(Number(fees.cleaningFee || 0) > 0 ||
                    Number(discounts.weeklyPercent || 0) > 0 ||
                    Number(discounts.monthlyPercent || 0) > 0) && (
                    <p className="ap-previewMeta">
                      {Number(fees.cleaningFee || 0) > 0 ? (
                        <>
                          <InlineIcon Icon={SprayCan} /> €{Number(fees.cleaningFee || 0)} cleaning
                        </>
                      ) : null}

                      {Number(discounts.weeklyPercent || 0) > 0 ? (
                        <>
                          {" "}
                          • <InlineIcon Icon={CalendarDays} /> {Number(discounts.weeklyPercent || 0)}% weekly
                        </>
                      ) : null}

                      {Number(discounts.monthlyPercent || 0) > 0 ? (
                        <>
                          {" "}
                          • <InlineIcon Icon={CalendarRange} /> {Number(discounts.monthlyPercent || 0)}% monthly
                        </>
                      ) : null}
                    </p>
                  )}

                  <p className="ap-previewMeta">
                    {t("addProperty.labels.rooms")}: <b>{rooms.length}</b> • {t("addProperty.labels.photos")}: <b>{previewUrls.length}</b>
                  </p>

                  <div
                    className="ap-checklist"
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.08)",
                      background: "rgba(0,0,0,0.02)",
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 900, marginBottom: 8 }}>
                      {t("addProperty.preview.required", { defaultValue: "Required" })}
                    </div>
                    <div>• {checklist.hasTitle ? "✅" : "⬜"} {t("addProperty.section.title", { defaultValue: "Title" })}</div>
                    <div>• {checklist.hasLocation ? "✅" : "⬜"} {t("addProperty.section.location", { defaultValue: "Location" })}</div>
                    <div>• {checklist.roomsOk ? "✅" : "⬜"} {t("addProperty.section.availability", { defaultValue: "Availability & price" })}</div>
                    <div>• {checklist.hasPhotos ? "✅" : "⬜"} {t("addProperty.section.photos", { defaultValue: "Photos" })}</div>
                    <div style={{ marginTop: 8, opacity: 0.8 }}>
                      {t("addProperty.preview.photosNotSaved", { defaultValue: "Note: photos are not saved in draft." })}
                    </div>
                  </div>

                  {!canSubmit && step === STEPS.length - 1 && <div className="ap-alert">{t("addProperty.preview.missingRequired")}</div>}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Sticky bottom CTA */}
      <div className="ap-ctaBar">
        <div className="ap-ctaInner">
          <div className="ap-ctaSummary">
            <div className="ap-ctaMain">
              €{basePrice || 0} / {t("addProperty.labels.perNight")}
            </div>
            <div className="ap-ctaSub">
              {rooms.length} {t("addProperty.labels.rooms").toLowerCase()} • {previewUrls.length}{" "}
              {t("addProperty.labels.photos").toLowerCase()}
            </div>
          </div>

          <div
            className="ap-splitCta"
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 0,
              borderRadius: 999,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.10)",
              background: "#fff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            }}
          >
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 0}
              className="ap-ctaBtn ap-ctaBtn--splitBack"
              style={{
                borderRadius: 0,
                minWidth: isNarrow ? 110 : 130,
                background: "#fff",
                color: "#222",
                boxShadow: "none",
                borderRight: "1px solid rgba(0,0,0,0.08)",
                opacity: step === 0 ? 0.55 : 1,
              }}
            >
              <ChevronLeft size={18} strokeWidth={2} /> {t("addProperty.nav.back", { defaultValue: "Back" })}
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!stepOk}
                className="ap-ctaBtn ap-ctaBtn--splitMain"
                style={{
                  borderRadius: 0,
                  minWidth: isNarrow ? 140 : 180,
                }}
              >
                {t("addProperty.nav.next", { defaultValue: "Next" })} <ChevronRight size={18} strokeWidth={2} />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => formRef.current?.requestSubmit?.()}
                disabled={!canSubmit}
                className="ap-ctaBtn ap-ctaBtn--splitMain"
                title={submitting ? t("addProperty.cta.creating") : ""}
                style={{
                  borderRadius: 0,
                  minWidth: isNarrow ? 140 : 180,
                }}
              >
                {submitting ? t("addProperty.cta.creating") : t("addProperty.cta.create")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}